import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckCircle, ArrowRight, Phone, Globe, Download, FileText, BookOpen, Calendar } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export const SummarySection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const keyPoints = [
    {
      title: 'SCC is Cost Efficient',
      description: 'Progressive discounts (up to 24% off), sibling bundles (up to 43% off), 1.5 hour sessions vs 1 hour elsewhere, and no GST on direct payments.',
    },
    {
      title: 'Focus on Development & Value',
      description: 'Quality coaching, longer 1.5 hour sessions, and a clear pathway from youth to senior football deliver real value for your investment.',
    },
    {
      title: 'Choose Your Commitment Level',
      description: 'Select 1, 2, or 3 sessions per week based on your family\'s budget and schedule. SCC provides flexible options to suit your needs.',
    },
    {
      title: 'Unique Lifetime Pathway',
      description: "Only club offering youth to senior football. Our SFL Division 1 champions show what's possible within SCC.",
    },
    {
      title: 'Family Friendly Options',
      description: 'Sibling discounts (20% for 2 children, 25% for 3 children), 5% member discount, and no GST on direct payments give you control over your costs.',
    },
  ];

  return (
    <section id="summary" className="min-h-screen py-24 px-6 bg-charcoal">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              Executive Summary
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              The Bottom Line
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              Key takeaways for SCC Football Academy
            </p>
          </motion.div>

          {/* Key Points */}
          <motion.div variants={itemVariants} className="grid gap-4 mb-12">
            {keyPoints.map((point, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="glass-card gold-border rounded-xl p-6 flex items-start gap-4"
              >
                <CheckCircle className="h-6 w-6 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-display text-lg text-primary mb-1">{point.title}</h4>
                  <p className="text-muted-foreground text-sm">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Final Message */}
          <motion.div variants={itemVariants}>
            <Card className="bg-primary/10 border-primary/30 overflow-hidden">
              <CardContent className="p-8 text-center">
                <h3 className="font-display text-2xl text-primary mb-4">
                  Building a Sustainable Future Together
                </h3>
                <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
                  Our new pricing structure ensures the Academy can thrive for generations to come, 
                  while remaining competitively priced in the Singapore market. We thank you for your 
                  continued support and understanding during this transition.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Globe className="h-4 w-4 mr-2" />
                    www.sccfootball.com
                  </Button>
                  <Button variant="outline" className="gold-border text-primary hover:bg-primary/10">
                    <Phone className="h-4 w-4 mr-2" />
                    8818 6675
                  </Button>
                  <a href="/parent-guide">
                    <Button variant="outline" className="gold-border text-primary hover:bg-primary/10">
                      <BookOpen className="h-4 w-4 mr-2" />
                      Parent Guide
                    </Button>
                  </a>
                  <a href="/SCC_Football_Academy_2025_Pricing.pdf" download>
                    <Button variant="outline" className="gold-border text-primary hover:bg-primary/10">
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </a>
                  <a href="/SCC_Football_Academy_2025_Pricing.pptx" download>
                    <Button variant="outline" className="gold-border text-primary hover:bg-primary/10">
                      <FileText className="h-4 w-4 mr-2" />
                      Download PPT
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Registration Links */}
          <motion.div variants={itemVariants} className="mt-8">
            <div className="glass-card gold-border rounded-xl p-6">
              <h4 className="font-display text-lg text-primary mb-4 text-center">Ready to Register?</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <a 
                  href="/register-academy"
                  className="flex items-center justify-between p-4 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors border-2 border-primary"
                >
                  <div>
                    <p className="text-primary font-bold text-lg">Register for Academy</p>
                    <p className="text-muted-foreground text-xs">Term packages & weekly sessions</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary" />
                </a>
                <a 
                  href="https://forms.gle/EqhaSnRPnSvLjpaYA" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 rounded-lg bg-charcoal hover:bg-charcoal-light transition-colors gold-border"
                >
                  <div>
                    <p className="text-card-foreground font-medium">Google Forms</p>
                    <p className="text-muted-foreground text-xs">Manual registration (bill to SCC account)</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Camps & Tournaments link */}
          <motion.div variants={itemVariants} className="mt-6">
            <a href="/register">
              <Card className="bg-gradient-to-r from-yellow-900/30 to-green-900/30 border-yellow-600/50 hover:border-yellow-500 transition-colors cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-yellow-400" />
                      </div>
                      <div>
                        <Badge className="bg-yellow-500 text-black mb-1">Camps & Tournaments</Badge>
                        <p className="text-lg font-display text-yellow-400">View Upcoming Events</p>
                        <p className="text-sm text-muted-foreground">Holiday camps, tournaments, and special clinics</p>
                      </div>
                    </div>
                    <ArrowRight className="h-6 w-6 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default SummarySection;
